import type { ChatSettings } from '../../settings/models/config.model';
import { ProposalPreview, UiAction } from '../../../../common/hooks/use-assistant-api.hook';

/**
 * The pure half of generative UI: everything a block decides that is not
 * drawing. Tested on its own so the rules — how a form edit moves a preview,
 * how a server-built step is translated — do not live inside JSX.
 */

/**
 * The settings a proposal's preview draws, with the vendor's edits applied.
 *
 * The server computed `settings` from the proposal as the model sent it; the
 * vendor may then drag a colour picker in the form. `bind` says which form
 * field moves which preview setting, so the preview follows the form rather
 * than the model's first guess.
 */
export function previewWithEdits(preview: ProposalPreview, edits: Record<string, unknown>): Record<string, any> {
    const settings = { ...(preview.settings ?? {}) };
    for (const [path, value] of Object.entries(edits ?? {})) {
        const key = preview.bind?.[path];
        if (!key) continue;
        settings[key] = key === 'buttonCorners' || key === 'cornerRadius' ? Number(String(value).replace(/px$/, '')) : value;
    }
    return settings;
}

/** The assistant's words for the chat settings, as the settings page's own shape. */
export function toChatSettings(s: Record<string, any>): ChatSettings {
    return {
        position: s.position === 'left' ? 'left' : 'right',
        greeting: s.greeting || '',
        label: s.label || '',
        color: s.color || '',
        textColor: s.textColor || '',
        hoverColor: s.hoverColor || '',
        hoverTextColor: s.hoverTextColor || '',
        prompts: {
            enabled: !!s.questionsOnPage,
            heading: s.questionsHeading || '',
            ctaLabel: s.questionsButtonLabel || '',
        } as any,
    };
}

/**
 * Keys for a server-built label, so a checklist reads in the vendor's language.
 *
 * Steps and their buttons carry a stable `id` (ADD_WEBSITE, SHOW_SNIPPET…);
 * the words are looked up under AGENTS.UI.ACTION.<ID>. A quick reply the model
 * wrote has no id — it is already in the vendor's language — and its own
 * label is used.
 */
export const actionKey = (action: UiAction, part: 'LABEL' | 'MESSAGE'): string | null =>
    'id' in action && action.id ? `AGENTS.UI.ACTION.${action.id}.${part}` : null;

export const stepKey = (goal: string | undefined, stepId: string, part: 'LABEL' | 'DETAIL'): string | null =>
    goal ? `AGENTS.UI.GUIDE.${goal.toUpperCase()}.${stepId.toUpperCase()}.${part}` : null;

/** Rows for a chart: one per x, one column per series — the shape recharts reads. */
export function chartRows(series: Array<{ name: string; points: Array<{ x: string; y: number }> }>): Array<Record<string, any>> {
    const order: string[] = [];
    const rows = new Map<string, Record<string, any>>();
    series.forEach((s, i) => {
        for (const p of s.points) {
            if (!rows.has(p.x)) {
                rows.set(p.x, { x: p.x });
                order.push(p.x);
            }
            rows.get(p.x)![`s${i}`] = p.y;
        }
    });
    return order.map((x) => rows.get(x)!);
}

/** A figure as it should read on an axis or a tile: 1240 → 1.2k. */
export function compact(value: number, unit?: string): string {
    const abs = Math.abs(value);
    const n = abs >= 1_000_000 ? `${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
        : abs >= 10_000 ? `${Math.round(value / 1000)}k`
            : abs >= 1_000 ? `${(value / 1000).toFixed(1).replace(/\.0$/, '')}k`
                : Number.isInteger(value) ? String(value) : value.toFixed(1);
    if (!unit) return n;
    return unit === '%' || unit === '€' || unit === '$' ? (unit === '%' ? `${n}%` : `${unit}${n}`) : `${n} ${unit}`;
}

/** Whether a change is good news: the tone when given, otherwise up is good. */
export function changeTone(change: number | undefined, tone?: 'good' | 'risk' | 'neutral'): 'good' | 'risk' | 'neutral' {
    if (tone) return tone;
    if (change === undefined || change === 0) return 'neutral';
    return change > 0 ? 'good' : 'risk';
}

/** How far through a checklist the vendor is, counting only steps that can be done. */
export function checklistProgress(steps: Array<{ status: string; detail?: string }>): { done: number; total: number } {
    const total = steps.length;
    const done = steps.filter((s) => s.status === 'done').length;
    return { done, total };
}
